/**
 * «Нет данных → указать» — ручной факт истории поля (AK-694, wireframe v2-18).
 *
 * Открывается кликом по пустой ячейке предшественника/препредшественника.
 * Сезон ЗАДАН КОЛОНКОЙ («факт 2026») и в форме не выбирается — атрибуция года
 * решена в момент клика, а не вычисляется заново на сабмите.
 *
 * Культура — ПОЛНЫЙ справочник `reference.crops`, а не номенклатура хозяйства:
 * предшественник — факт прошлого, и «Пшеница озимая» могла расти на поле до
 * того, как попала в номенклатуру нового сезона (прецедент мастера техкарт,
 * #842). Алфавит + поиск — сквозной паттерн выпадашек-справочников.
 *
 * Площадь по умолчанию — контур поля, и пустое поле означает именно «по
 * контуру» (лестница `sowing_area_ha` возьмёт её сама). Цифра рукой — только
 * для одного поля: при массовом применении одна цифра на десять контуров была
 * бы враньём для девяти, поэтому ввод гаснет с объяснением.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCropsReference } from '@/lib/map/useCropsReference';
import { useAddFieldHistory, type VariantField } from '@/lib/planning/planCrops';
import { formatHa } from '@/lib/planning/planVariant';
import { matchesQuery } from '../plan-variant/AddCropDialog';

/**
 * Разбор площади: три исхода, различённые ТИПОМ (правило «пусто ≠ мусор»).
 * Пусто — осознанный выбор «по контуру поля», мусор и ноль — ошибка словами.
 */
export function parseAreaDraft(
  draft: string,
): { ok: true; value: number | null } | { ok: false; error: string } {
  const trimmed = draft.trim();
  if (trimmed === '') return { ok: true, value: null };
  const parsed = Number(trimmed.replace(',', '.').replace(/\s/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return {
      ok: false,
      error: 'Площадь — число больше нуля, например 98,5. Пустое поле — «по контуру поля».',
    };
  }
  return { ok: true, value: parsed };
}

export function HistoryEntryDialog({
  scenarioId,
  field,
  seasonYear,
  selectedOthers,
  open,
  onClose,
}: {
  scenarioId: string;
  /** Поле, по чьей ячейке кликнули. */
  field: VariantField;
  /** Год колонки — сезон, В КОТОРЫЙ пишется факт. */
  seasonYear: number;
  /** Остальные выбранные чекбоксами поля — кандидаты массового применения. */
  selectedOthers: VariantField[];
  open: boolean;
  onClose: () => void;
}) {
  const crops = useCropsReference();
  const save = useAddFieldHistory();

  const [cropId, setCropId] = useState<string | null>(null);
  const [cropQuery, setCropQuery] = useState('');
  // Черновик площади стартует с контура: агроном чаще всего подтверждает его,
  // а не вводит с нуля. Отправляем цифру только если её действительно правили.
  const [areaDraft, setAreaDraft] = useState(() => String(field.area_ha));
  const [areaError, setAreaError] = useState<string | null>(null);
  const [applyToSelected, setApplyToSelected] = useState(false);

  const cropOptions = useMemo(
    () =>
      [...(crops.data ?? [])]
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [crops.data],
  );
  const shownCrops = cropOptions.filter((c) => matchesQuery(c.name, cropQuery));
  const cropName = cropOptions.find((c) => c.id === cropId)?.name ?? '';

  const massMode = applyToSelected && selectedOthers.length > 0;

  const close = () => {
    setCropId(null);
    setCropQuery('');
    setAreaDraft(String(field.area_ha));
    setAreaError(null);
    setApplyToSelected(false);
    onClose();
  };

  const submit = () => {
    if (!cropId || save.isPending) return;
    let areaHa: number | null = null;
    if (!massMode) {
      const parsed = parseAreaDraft(areaDraft);
      if (!parsed.ok) {
        // Набранное НЕ выбрасываем: человек видит, что именно ввёл, и правит.
        setAreaError(parsed.error);
        return;
      }
      // Неизменённый дефолт — это «по контуру», а не площадь рукой: рука
      // замораживает цифру навсегда, контур продолжает жить с полем.
      areaHa = parsed.value !== null && parsed.value !== field.area_ha ? parsed.value : null;
    }
    setAreaError(null);
    save.mutate(
      {
        scenarioId,
        seasonYear,
        cropId,
        cropName,
        fieldIds: massMode
          ? [field.id, ...selectedOthers.map((f) => f.id)]
          : [field.id],
        areaHa,
      },
      { onSuccess: () => close() },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{`Поле «${field.name}» · что росло в сезоне ${seasonYear}?`}</DialogTitle>
          <DialogDescription>
            Запишем как обычный факт сезона {seasonYear} — он сразу станет
            предшественником в плане и в фильтрах. Ночная синхронизация ручную
            запись не перепишет.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="history-crop-search">Культура из справочника</Label>
              <Input
                id="history-crop-search"
                value={cropQuery}
                onChange={(e) => setCropQuery(e.target.value)}
                placeholder="Поиск по названию"
                autoFocus
              />
              {crops.isLoading && <Skeleton className="h-24 w-full" />}
              {/* Ошибка ≠ пустота: «культур нет» при упавшем запросе — враньё. */}
              {crops.isError && (
                <p className="text-sm text-destructive">
                  Не удалось загрузить справочник культур — попробуйте ещё раз.
                </p>
              )}
              {!crops.isLoading && !crops.isError && cropOptions.length > 0 && (
                <ScrollArea className="max-h-56 rounded-md border border-border">
                  <div className="flex flex-col p-1">
                    {shownCrops.map((crop) => (
                      <button
                        key={crop.id}
                        type="button"
                        aria-pressed={crop.id === cropId}
                        onClick={() => setCropId(crop.id)}
                        className={
                          crop.id === cropId
                            ? 'rounded-md bg-primary/10 px-3 py-2 text-left text-sm font-medium text-primary'
                            : 'rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent'
                        }
                      >
                        {crop.name}
                      </button>
                    ))}
                    {shownCrops.length === 0 && (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        По запросу ничего не нашлось.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="history-area">Площадь, га</Label>
              <Input
                id="history-area"
                inputMode="decimal"
                value={massMode ? '' : areaDraft}
                disabled={massMode}
                placeholder={massMode ? 'по контуру каждого поля' : undefined}
                aria-invalid={areaError ? true : undefined}
                className={
                  areaError ? 'border-destructive focus-visible:ring-destructive' : undefined
                }
                onChange={(e) => {
                  setAreaDraft(e.target.value);
                  if (areaError) setAreaError(null);
                }}
              />
              {areaError && (
                <p role="alert" className="text-xs text-destructive">
                  {areaError}
                </p>
              )}
              {!areaError && (
                <p className="text-xs text-muted-foreground">
                  {massMode
                    ? 'При массовом применении площадь каждого поля берётся по его контуру.'
                    : `По контуру поля — ${formatHa(field.area_ha)}. Оставьте как есть или поправьте.`}
                </p>
              )}
            </div>

            {selectedOthers.length > 0 && (
              <label className="flex items-start gap-2 text-sm text-foreground">
                <Checkbox
                  checked={applyToSelected}
                  onCheckedChange={(v) => setApplyToSelected(v === true)}
                  aria-label={`Применить также к выбранным полям (отмечено ${selectedOthers.length})`}
                />
                <span>
                  Применить также к выбранным полям (отмечено {selectedOthers.length}).
                  <span className="block text-xs text-muted-foreground">
                    Поля, где факт за {seasonYear} уже есть, пропустим и назовём — молча
                    ничего не перепишем.
                  </span>
                </span>
              </label>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={close}>
              Отмена
            </Button>
            <Button type="submit" disabled={!cropId || save.isPending}>
              {save.isPending ? 'Записываем…' : 'Записать в историю'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
